import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Box,
  Typography,
  Alert,
  Chip,
  Stack,
  CircularProgress,
  Autocomplete
} from '@mui/material';
import { MergeType, Warning } from '@mui/icons-material';
import { categoriesApi, transactionsApi } from '../services/api';

const MergeCategoriesModal = ({ open, onClose, categories, onMergeComplete }) => {
  const [sourceCategories, setSourceCategories] = useState([]);
  const [destinationCategory, setDestinationCategory] = useState('');
  const [transactionCount, setTransactionCount] = useState(null);
  const [categoryBreakdown, setCategoryBreakdown] = useState({});
  const [loading, setLoading] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [fetchingCount, setFetchingCount] = useState(false);
  const [countError, setCountError] = useState(null);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (open) {
      setSourceCategories([]);
      setDestinationCategory('');
      setTransactionCount(null);
      setCategoryBreakdown({});
      setConfirmed(false);
      setCountError(null);
    }
  }, [open]);

  // Fetch transaction count when source categories change
  useEffect(() => {
    const fetchTransactionCount = async () => {
      if (sourceCategories.length === 0) {
        setTransactionCount(null);
        setCategoryBreakdown({});
        setCountError(null);
        return;
      }

      setFetchingCount(true);
      setCountError(null);
      try {
        // Get count of transactions for all source categories
        const promises = sourceCategories.map(async (categoryName) => {
          const result = await transactionsApi.getAll({ category: categoryName, limit: 1 });
          // The API returns count in pagination.totalCount
          const count = result.data?.pagination?.totalCount || result.data?.summary?.totalTransactions || 0;
          return {
            category: categoryName,
            count: count
          };
        });
        const results = await Promise.all(promises);
        
        // Build breakdown object
        const breakdown = {};
        let total = 0;
        results.forEach(({ category, count }) => {
          breakdown[category] = count;
          total += count;
        });
        
        setCategoryBreakdown(breakdown);
        setTransactionCount(total);
      } catch (error) {
        console.error('Error fetching transaction count:', error);
        setCountError(error.message || 'Failed to fetch transaction count');
        setTransactionCount(null);
        setCategoryBreakdown({});
      } finally {
        setFetchingCount(false);
      }
    };

    fetchTransactionCount();
  }, [sourceCategories]);

  const handleSourceChange = (event) => {
    const value = event.target.value;
    const selectedCategories = typeof value === 'string' ? value.split(',') : value;
    setSourceCategories(selectedCategories);
    
    // If a default category is selected, auto-populate destination with it
    const selectedCategoryObjects = categories.filter(cat => selectedCategories.includes(cat.name));
    const defaultInSelection = selectedCategoryObjects.find(cat => cat.isDefault);
    
    if (defaultInSelection) {
      setDestinationCategory(defaultInSelection.name);
    } else {
      // If no default is selected and destination was locked, clear it
      const wasDefaultLocked = categories.find(cat => cat.name === destinationCategory && cat.isDefault);
      if (wasDefaultLocked && !selectedCategories.includes(wasDefaultLocked.name)) {
        setDestinationCategory('');
      }
    }
  };

  const handleMerge = async () => {
    if (sourceCategories.length === 0) {
      return;
    }

    if (!destinationCategory.trim()) {
      return;
    }

    setLoading(true);
    try {
      const response = await categoriesApi.merge(sourceCategories, destinationCategory);
      onMergeComplete(response.data);
      onClose();
    } catch (error) {
      console.error('Error merging categories:', error);
      alert(error.response?.data?.message || 'Failed to merge categories');
    } finally {
      setLoading(false);
    }
  };

  // Check if a default category is selected in sources
  const selectedCategoryObjects = categories.filter(cat => sourceCategories.includes(cat.name));
  const defaultInSelection = selectedCategoryObjects.find(cat => cat.isDefault);
  
  // Determine which categories should be disabled in the source dropdown
  const isCategoryDisabled = (category) => {
    // If a default is already selected, disable all other defaults
    if (defaultInSelection && category.isDefault && category.name !== defaultInSelection.name) {
      return true;
    }
    return false;
  };

  const availableCategories = categories;

  const canMerge = sourceCategories.length >= 1 && 
                   destinationCategory.trim().length > 0 && 
                   confirmed;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <MergeType />
        Merge Categories
      </DialogTitle>
      
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <Alert severity="info">
            Merge multiple categories into one. Select the categories to consolidate, then choose 
            which name to keep (can be one of the selected categories or a new name). All saved transactions 
            will be updated to use the destination category.
          </Alert>
          <Alert severity="warning" sx={{ py: 1 }}>
            <Typography variant="body2">
              <strong>Note:</strong> This merge only applies to saved transactions. Pending transactions 
              (currently in review) will not be affected. You can update their categories during the review process.
            </Typography>
          </Alert>
          
          {defaultInSelection ? (
            <Alert severity="success" sx={{ py: 1 }}>
              <Typography variant="body2">
                <strong>✓ Default category "{defaultInSelection.name}" selected</strong>
              </Typography>
              <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
                Other default categories are now disabled. The destination is locked to "{defaultInSelection.name}" 
                to preserve this default category.
              </Typography>
            </Alert>
          ) : (
            <Alert severity="info" sx={{ py: 1 }}>
              <Typography variant="body2">
                <strong>Default Category Rules:</strong>
              </Typography>
              <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
                • You cannot merge multiple default categories together
              </Typography>
              <Typography variant="caption" component="div">
                • If merging a default category with others, you must keep the default category's name as the destination
              </Typography>
            </Alert>
          )}

          {/* Source Categories */}
          <FormControl fullWidth>
            <InputLabel>Source Categories (to merge)</InputLabel>
            <Select
              multiple
              value={sourceCategories}
              onChange={handleSourceChange}
              input={<OutlinedInput label="Source Categories (to merge)" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => {
                    const cat = categories.find(c => c.name === value);
                    return (
                      <Chip 
                        key={value} 
                        label={value} 
                        size="small"
                        color={cat?.isDefault ? 'primary' : 'default'}
                      />
                    );
                  })}
                </Box>
              )}
            >
              {categories.map((category) => (
                <MenuItem 
                  key={category.id} 
                  value={category.name}
                  disabled={isCategoryDisabled(category)}
                >
                  <Checkbox 
                    checked={sourceCategories.indexOf(category.name) > -1}
                    disabled={isCategoryDisabled(category)}
                  />
                  <ListItemText 
                    primary={category.name}
                    secondary={category.isDefault ? 'Default' : 'Custom'}
                    sx={{
                      opacity: isCategoryDisabled(category) ? 0.5 : 1
                    }}
                  />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Transaction Count Preview */}
          {sourceCategories.length > 0 && (
            <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
              {fetchingCount ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2" color="text.secondary">
                    Counting transactions...
                  </Typography>
                </Box>
              ) : countError ? (
                <Alert severity="warning" sx={{ py: 0 }}>
                  <Typography variant="body2">
                    Error: {countError}
                  </Typography>
                </Alert>
              ) : transactionCount !== null ? (
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>{transactionCount}</strong> transaction{transactionCount !== 1 ? 's' : ''} will be updated
                  </Typography>
                  {Object.keys(categoryBreakdown).length > 0 && (
                    <Box sx={{ mt: 1, pl: 2 }}>
                      {Object.entries(categoryBreakdown).map(([category, count]) => (
                        <Typography key={category} variant="caption" display="block" color="text.secondary">
                          • {category}: {count} transaction{count !== 1 ? 's' : ''}
                        </Typography>
                      ))}
                    </Box>
                  )}
                  {transactionCount === 0 && (
                    <Alert severity="info" sx={{ mt: 1 }}>
                      <Typography variant="caption">
                        These categories have no transactions yet. You can still merge them to consolidate your category list.
                      </Typography>
                    </Alert>
                  )}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Unable to count transactions
                </Typography>
              )}
            </Box>
          )}

          {/* Destination Category */}
          <Autocomplete
            freeSolo
            disabled={!!defaultInSelection}
            options={availableCategories.map(cat => cat.name)}
            value={destinationCategory}
            onInputChange={(event, newValue) => {
              // Only allow changes if no default is selected
              if (!defaultInSelection) {
                setDestinationCategory(newValue);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Destination Category"
                placeholder="Select existing or type new category name"
                helperText={
                  defaultInSelection 
                    ? `🔒 Locked to "${defaultInSelection.name}" because it's a default category`
                    : "The category that will contain all merged transactions"
                }
                required
              />
            )}
          />

          {/* Warning */}
          {sourceCategories.length > 0 && destinationCategory && (
            <Alert severity="warning" icon={<Warning />}>
              <Typography variant="body2" gutterBottom>
                <strong>This action cannot be undone!</strong>
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                All transactions from: <strong>{sourceCategories.join(', ')}</strong>
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Will be consolidated into: <strong>{destinationCategory}</strong>
              </Typography>
              {sourceCategories.includes(destinationCategory) ? (
                <Typography variant="body2">
                  Other categories will be removed, keeping only: <strong>{destinationCategory}</strong>
                </Typography>
              ) : (
                <Typography variant="body2">
                  All source categories will be removed after merge.
                </Typography>
              )}
            </Alert>
          )}

          {/* Confirmation Checkbox */}
          {sourceCategories.length > 0 && destinationCategory && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: -1 }}>
              <Checkbox
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
              />
              <Typography variant="body2">
                I understand this action cannot be undone
              </Typography>
            </Box>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleMerge}
          disabled={!canMerge || loading}
          startIcon={loading ? <CircularProgress size={20} /> : <MergeType />}
        >
          {loading ? 'Merging...' : 'Merge Categories'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MergeCategoriesModal;

